import React from 'react';
import _ from 'lodash';
import {
  creatRepositoriesPageAddRepositoryModalOptions,
  createPersonalAccessTokenModalOptions,
  createHostedGitProviderModalOptions
} from 'modals/modal-options-factory';
import * as TrackingService from 'common/services/tracking.service';
import {
  TRACKING,
  PROVIDERS_FETCH_LARGE_REPOS_FF,
  TOKEN_VERSION_FOR_BB_GITSTREAM_INTEGRATION
} from 'common/constants/constants';
import {
  onReauthorizeClick,
  getSuccessSnackbarTitle,
  processingRepos
} from '../repositories.service';
import {
  redirectToProvider,
  debouncedToggleSnackBar,
  getOrganizationFromBaseUrl
} from 'common/utils/utils';
import AppRouter from 'common/AppRouter';
import { FEATURE_FLAGS_NAMES, SENSOR_NAMES } from '@linearb/linjs-common';
import { createModalWrapper } from '../repositoriesSections';
import useSettingsService from 'components/hooks/useSettingsService';

const NO_PAT_PROVIDERS = [SENSOR_NAMES.BITBUCKET];

const withGitIntegrationsModals = WrappedComponent => props => {
  const {
    toggleSnackBar,
    repositories,
    createRepositories,
    account,
    toggleModal,
    setIsProcessingRepos,
    deleteSensorAuthorization,
    gitIntegrations,
    url,
    getRepositories
  } = props;

  const { getSettingOrDefinitionValue } = useSettingsService();
  const openRepositoriesModal = ({
    sensorAuth,
    isAddIntegrationFlow = false,
    isConnectGitStreamFlow
  }) => {
    const sensor_authorization_id = sensorAuth?.id;
    const name = sensorAuth?.name;
    const sensor_name = sensorAuth?.sensor_name;
    const isFetchLargeRepos = getSettingOrDefinitionValue({
      name: _.get(PROVIDERS_FETCH_LARGE_REPOS_FF, [sensor_name])
    });

    const isGithubFetchAllRepoTypesFF = getSettingOrDefinitionValue({
      name: FEATURE_FLAGS_NAMES.githubFetchAllRepoTypes
    });

    if (!isAddIntegrationFlow) {
      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
        page: TRACKING.PAGES.SETTINGS,
        page_tab: TRACKING.PAGE_TABS.GIT_INTEGRATIONS,
        click_type: TRACKING.CLICK.ADD_REPOS
      });
    }
    toggleModal(
      creatRepositoriesPageAddRepositoryModalOptions(
        {
          repositories,
          createRepositories,
          account,
          toggleModal,
          hideDots: true,
          hideBackdrop: false,
          openReAuthModal: () => {
            openReauthorizeModal({
              id: sensor_authorization_id,
              sensor_name: (sensor_name || '').toLowerCase(),
              url,
              gitIntegrations,
              toggleModal
            });
          },
          deleteSensorAuthorization: sensor_id =>
            deleteSensorAuthorization(sensor_id),
          onSubmitCb: ({ messageId, repositories, available_repositories }) => {
            if (isFetchLargeRepos && messageId) {
              processingRepos({
                setIsProcessingRepos,
                getRepositories
              });

              debouncedToggleSnackBar(
                'Repositories added successfully.',
                'NOTE: It might take a couple of hours to see updated data reflecting this change.',
                true,
                toggleSnackBar
              );
            } else if (
              !isFetchLargeRepos &&
              repositories &&
              repositories.length
            ) {
              processingRepos({
                setIsProcessingRepos,
                getRepositories
              });

              debouncedToggleSnackBar(
                getSuccessSnackbarTitle(repositories),
                'NOTE: It might take a couple of hours to see updated data reflecting this change.',
                true,
                toggleSnackBar
              );
              TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                page: TRACKING.PAGES.SETTINGS,
                page_tab: TRACKING.PAGE_TABS.SELECT_REPOS,
                click_type: TRACKING.CLICK.SAVE,
                num_of_repos: repositories.length,
                num_avail_repos: available_repositories.length
              });
            } else {
              debouncedToggleSnackBar(
                `Failed to add repositories`,
                '',
                false,
                toggleSnackBar
              );
            }
            toggleModal();
          },
          hideLogo: true,
          isFetchLargeRepos,
          isGithubFetchAllRepoTypesFF,
          isConnectGitStreamFlow,
          ...(sensor_authorization_id ? { sensor_authorization_id } : {})
        },
        {
          ContentWrapper: name ? createModalWrapper(name, toggleModal) : null,
          showCloseButton: false
        }
      )
    );
  };

  const onConnectProvider = ({ provider, isPAT, sensorAuth }) => {
    if (isPAT) {
      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
        page: TRACKING.PAGES.SETTINGS,
        page_tabs: TRACKING.PAGE_TABS.GIT_INTEGRATIONS,
        click_type: TRACKING.CLICK.PAT
      });
      openRepositoriesModal({
        sensorAuth,
        isAddIntegrationFlow: true
      });
    } else {
      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
        page: TRACKING.PAGES.SETTINGS,
        page_tabs: TRACKING.PAGE_TABS.GIT_INTEGRATIONS,
        click_type: TRACKING.CLICK.OAUTH
      });
      redirectToProvider(provider, {
        redirectPath: `${AppRouter.REPOSITORIES.path}?isOrgSettings=true`
      });
    }
  };

  const openPATModal = ({
    provider,
    withPAT,
    onBack,
    modalOptions,
    onSubmit = onConnectProvider,
    sensorAuthId,
    isCloudProvider = true,
    baseUrl,
    toggleModal,
    azureOrganizationName,
    gitStreamEnabled = false
  }) => {
    toggleModal(
      createPersonalAccessTokenModalOptions(
        {
          baseUrl,
          provider,
          withPAT,
          isCloudProvider,
          onSubmit,
          onBack,
          sensorAuthId,
          azureOrganizationName,
          gitStreamEnabled
        },
        modalOptions
      )
    );
  };

  const getModalOptions = ({
    toggleModal,
    isReauth = false,
    gitIntegration
  }) => ({
    showCloseButton: false,
    ContentWrapper: createModalWrapper(
      isReauth ? gitIntegration?.name || '' : 'ADD INTEGRATION',
      toggleModal
    ),
    modalParams: { hideBackdrop: false }
  });

  const openReauthorizeModal = ({
    id,
    sensor_name,
    url,
    gitIntegrations,
    toggleModal,
    getSensorAuthorizations
  }) => {
    const baseUrl = _.get(
      _.find(gitIntegrations, integration => integration.id === id),
      'base_url',
      ''
    );
    const isV3BBSensorAuthorization =
      gitIntegrations.find(s => s.id === id)?.profile?.token_version ===
      TOKEN_VERSION_FOR_BB_GITSTREAM_INTEGRATION;

    const azureOrganizationName = getOrganizationFromBaseUrl(baseUrl);
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.SETTINGS,
      page_tab: TRACKING.PAGE_TABS.REPOS,
      click_type: TRACKING.CLICK.REAUTH_MODAL_OPENED
    });
    const gitIntegration = gitIntegrations.find(s => s.id === id);
    const { is_self_hosted } = gitIntegration;

    const withPAT = !NO_PAT_PROVIDERS.includes(sensor_name) || is_self_hosted;

    openPATModal({
      sensorAuthId: id,
      ...(url ? { baseUrl: url } : {}),
      provider: sensor_name,
      withPAT,
      isCloudProvider: !is_self_hosted,
      onBack: null,
      modalOptions: getModalOptions({
        toggleModal,
        isReauth: true,
        gitIntegration
      }),
      onSubmit: () =>
        onReauthorizeClick({
          id,
          sensor_name,
          gitStreamEnabled: isV3BBSensorAuthorization
        }),
      toggleModal,
      azureOrganizationName
    });
  };

  const createHostedModal = ({
    provider,
    onBack,
    toggleModal,
    modalOptions
  }) => {
    toggleModal(
      createHostedGitProviderModalOptions(
        {
          provider,
          onBack,
          onSubmit: ({ baseUrl }) =>
            toggleModal(
              createPersonalAccessTokenModalOptions(
                {
                  provider,
                  baseUrl,
                  withPAT: true,
                  isCloudProvider: false,
                  onSubmit: onConnectProvider,
                  onBack
                },
                modalOptions
              )
            )
        },
        modalOptions
      )
    );
  };

  return (
    <WrappedComponent
      {...props}
      createHostedModal={createHostedModal}
      getModalOptions={getModalOptions}
      openRepositoriesModal={openRepositoriesModal}
      onConnectProvider={onConnectProvider}
      openPATModal={openPATModal}
      openReauthorizeModal={openReauthorizeModal}
      createModalWrapper={createModalWrapper}
      gitIntegrations={gitIntegrations}
    />
  );
};

export default withGitIntegrationsModals;
